import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Check } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState(() => (params.get("ref") || "").toUpperCase());
  const [refValid, setRefValid] = useState(null); // null | {valid, referrer_name}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const code = refCode.trim().toUpperCase();
    if (code.length !== 6) { setRefValid(null); return; }
    let cancelled = false;
    api.get(`/referrals/validate/${code}`).then(({ data }) => {
      if (!cancelled) setRefValid(data);
    }).catch(() => !cancelled && setRefValid({ valid: false }));
    return () => { cancelled = true; };
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(name, email, password, refCode.trim().toUpperCase() || null);
      toast.success(refValid?.valid ? `Welcome! Bonus preview unlocked via ${refValid.referrer_name}` : "Welcome to Porcelain");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[440px] mx-auto px-6 py-20" data-testid="signup-page">
      <div className="font-mono-label text-primary">Create account</div>
      <h1 className="font-serif-display text-5xl mt-3 font-light leading-none tracking-tighter">Pick up the instrument.</h1>
      <p className="mt-4 text-muted-foreground">Free signup. Full curriculum. Track progress across every lesson.</p>
      <form onSubmit={submit} className="mt-10 space-y-5">
        <div>
          <label className="font-mono-label text-muted-foreground block mb-2">Full name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-white border border-border px-4 py-3 outline-none focus:border-primary"
            style={{ borderRadius: 2 }} data-testid="signup-name" />
        </div>
        <div>
          <label className="font-mono-label text-muted-foreground block mb-2">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-border px-4 py-3 outline-none focus:border-primary"
            style={{ borderRadius: 2 }} data-testid="signup-email" />
        </div>
        <div>
          <label className="font-mono-label text-muted-foreground block mb-2">Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-border px-4 py-3 outline-none focus:border-primary"
            style={{ borderRadius: 2 }} data-testid="signup-password" />
        </div>
        <div>
          <label className="font-mono-label text-muted-foreground block mb-2 flex items-center gap-2">
            Referral code (optional) <Gift className="w-3 h-3 text-primary" />
          </label>
          <input
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toUpperCase())}
            maxLength={6}
            placeholder="ABCD12"
            className="w-full bg-white border border-border px-4 py-3 outline-none focus:border-primary uppercase tracking-wider"
            style={{ borderRadius: 2 }}
            data-testid="signup-referral"
          />
          {refValid && refCode.length === 6 && (
            <div className={`mt-2 font-mono-label flex items-center gap-2 ${refValid.valid ? "text-primary" : "text-destructive"}`} data-testid="signup-ref-status">
              {refValid.valid ? (<><Check className="w-3 h-3" /> Valid — bonus preview will be unlocked (from {refValid.referrer_name})</>) : "Invalid code"}
            </div>
          )}
        </div>
        <button type="submit" disabled={loading} className="btn-oxblood w-full disabled:opacity-50" data-testid="signup-submit">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        Already learning? <Link to="/login" className="text-primary hover:underline" data-testid="signup-login-link">Sign in</Link>
      </div>
    </div>
  );
}
